import { useCallback, useEffect, useState } from 'react';

type Value = string | ((value: string) => string);

export default function useLocalStorage(key: string, defaultValue: string): [string, (value: Value) => void] {
    const [storedValue, setStoredValue] = useState(() => {
        try {
            const item = window.localStorage.getItem(key);

            return item ? item : defaultValue;
        } catch {
            return defaultValue;
        }
    });

    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === key && e.newValue && e.newValue !== storedValue) setStoredValue(e.newValue);
        };
        window.addEventListener('storage', onStorage);

        return () => {
            window.removeEventListener('storage', onStorage);
        };
    }, [key, storedValue]);

    const setValue = useCallback(
        (value: Value) => {
            try {
                const valueToStore = value instanceof Function ? value(storedValue) : value;
                if (valueToStore !== storedValue) {
                    setStoredValue(valueToStore);
                    window.localStorage.setItem(key, valueToStore);
                    window.dispatchEvent(new StorageEvent('storage', { key, newValue: valueToStore }));
                }
            } catch (error) {
                console.error(error);
            }
        },
        [key, storedValue]
    );

    return [storedValue, setValue];
}
